import { useState, useMemo } from 'react'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, Search } from 'lucide-react'
import Breadcrumb from '@/components/ui/Breadcrumb'
// import StrategicPillars from '@/components/sections/StrategicPillars'
import RecentArticles from '@/components/sections/RecentArticles'
import Newsletter from '@/components/sections/Newsletter'
import { useAllWebPages } from '@/lib/api/hooks/web-pages'
import FullPageSpinner from '@/components/ui/FullPageSpinner'

import { useMemberCountries } from '@/lib/api/hooks/member-country'
import { useAllRegions } from '@/lib/api/hooks/region'
import type { MemberCountry } from '@/lib/api/types'
import { parseHtmlString } from '@/utils/helpers/html'

interface RegionTabProps {
  region: string
  isActive: boolean
  onClick: () => void
}

const RegionTab = ({ region, isActive, onClick }: RegionTabProps) => (
  <button
    onClick={onClick}
    className={`flex-1 p-4 text-base font-satoshi transition-colors ${isActive
      ? 'bg-wine-100 text-white font-bold'
      : 'bg-white text-gray-900 font-normal hover:bg-gray-50'
      }`}
  >
    {region}
  </button>
)

interface CountryCardProps {
  name: string
  fullName: string
  flag: string
  onClick: () => void
}

const CountryCard = ({ name, fullName, flag, onClick }: CountryCardProps) => (
  <div className="w-48 h-[215px] group cursor-pointer transition-all duration-300 bg-gray-50 pb-10" onClick={onClick}>
    <div className="w-40 mx-auto pt-9 flex flex-col items-center gap-4">
      {flag && flag !== 'N/A' ? (
        <img
          src={flag}
          alt={`${name} flag`}
          className="w-24 h-16 object-cover"
          onError={(e) => {
            // Hide image if it fails to load
            e.currentTarget.style.display = 'none'
          }}
        />
      ) : (
        <div className="w-24 h-16 bg-gray-200 flex items-center justify-center">
          <span className="text-xs text-gray-400">No flag</span>
        </div>
      )}
      <h3 className="text-sm font-medium font-satoshi text-gray-900 text-center leading-tight h-10 flex items-center">
        {fullName}
      </h3>
    </div>
    <div className="flex justify-center pt-2">
      <button
        onClick={onClick}
        className="inline-flex items-center gap-2 px-4 py-2 group-hover:text-wine-100 transition-colors"
      >
        <ArrowRight className="w-6 h-6 hidden md:block" />
      </button>
    </div>
  </div>
)


const normalizeRegionName = (regionName: string): string => {
  return regionName.toUpperCase()
}

// Geographic order for politically neutral display (North → East → West → Central → South)
const REGION_ORDER: Record<string, number> = {
  'northern': 2,
  'north': 2,
  'eastern': 4,
  'east': 4,
  'western': 5,
  'west': 5,
  'central': 1,
  'southern': 3,
  'south': 3,
}

const getRegionOrder = (regionName: string): number => {
  const lowerName = regionName.toLowerCase()
  for (const [key, order] of Object.entries(REGION_ORDER)) {
    if (lowerName.includes(key)) {
      return order
    }
  }
  return 99 // Unknown regions go last
}

const MemberCountries = () => {
  const navigate = useNavigate()
  const [activeRegionId, setActiveRegionId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')


  const { data, isFetching, isError, refetch } = useAllWebPages()
  const memberCountriesWebPageData = data?.data && data?.data.find((page) =>
    page.codeName?.toLowerCase() === 'member_countries' ||
    page.websitePageName?.toLowerCase() === 'member countries'
  )?.content || null


  const { data: regionsData, isLoading: isLoadingRegions, error: regionsError } = useAllRegions()
  const regions = useMemo(() => {
    const rawRegions = regionsData?.data || []
    // Sort by geographic order: North → East → West → Central → South
    return [...rawRegions].sort((a, b) => getRegionOrder(a.regionName) - getRegionOrder(b.regionName))
  }, [regionsData?.data])

  // Set first region as active when regions are loaded
  React.useEffect(() => {
    if (regions.length > 0 && !activeRegionId) {
      const firstRegionId = regions[0].regionId
      setActiveRegionId(firstRegionId)
    }
  }, [regions, activeRegionId])

  // Fetch member countries for the active region (using regionTag)
  const activeRegion = useMemo(() => {
    return regions.find(r => r.regionId === activeRegionId)
  }, [regions, activeRegionId])

  const filters = useMemo(() =>
    activeRegion ? { regionTag: activeRegion.regionTag } : {},
    [activeRegion]
  )
  const isQueryEnabled = !!activeRegion

  const { data: memberCountriesApiData, isLoading: isLoadingCountries, error: countriesError } = useMemberCountries(
    { page: 1, size: 100 },
    filters,
    isQueryEnabled
  )



  const countries = useMemo(() => {
    if (!memberCountriesApiData?.data?.data) {
      return []
    }

    const countriesData = memberCountriesApiData.data.data



    if (!Array.isArray(countriesData)) {
      console.warn('Expected countries data to be an array, got:', countriesData)
      return []
    }

    return countriesData.map((country: MemberCountry) => {
      // Use abbreviation for short name, full countryName for full display
      return {
        id: country.countryId,
        name: country.abbreviation,
        fullName: country.countryName,
        flag: country.flagUrl !== 'N/A' ? country.flagUrl : ''
      }
    })
  }, [memberCountriesApiData])

  const handleCountryClick = (countryId: string) => {
    navigate(`/about/member-countries/${countryId}`)
  }

  const filteredCountries = useMemo(() => {
    return countries.filter(country =>
    (searchQuery === '' || country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      country.fullName.toLowerCase().includes(searchQuery.toLowerCase()))
    )
  }, [countries, searchQuery])

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'About Us', href: '/about' },
    { label: 'Member Countries', href: '/about/member-countries', active: true }
  ]

  if (isFetching) {
    return <FullPageSpinner />;
  }

  if (isError) {
    return <div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4">
      <p>Error loading page.</p>
      <button onClick={() => refetch()} className="px-4 py-2 bg-forestGreen-100 text-white rounded">Retry</button>
    </div>;
  }

  if (!memberCountriesWebPageData) {
    return <div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4">
      <p>Page content not found.</p>
    </div>;
  }
  return (
    <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
      {/* Breadcrumb */}
      <div className=" mb-5   ">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Hero Image */}
      <div className="mb-16">
        <img
          src={memberCountriesWebPageData?.MEMBER_COUNTRIES_BANNER?.fileUrl ?? "https://res.cloudinary.com/dlwegkhme/image/upload/v1755760296/93411e5e11cca7d6af1a48e1d3597d83bea53c12_1_nbhkmv.png"}
          alt="African Space Agency Member Countries"
          className="w-full h-64 sm:h-80 md:h-96 lg:h-[480px] object-cover rounded"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-16">
        {/* Main Content */}
        <div className="lg:col-span-2">
          {/* Introduction */}
          <div className="mb-16 space-y-6">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-normal font-pt-serif text-gray-900 leading-tight lg:leading-12">
              {memberCountriesWebPageData?.MEMBER_COUNTRIES?.title ?? "Member Countries"}
            </h1>
            <div className="space-y-12">
              <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed max-w-2xl">
                <span dangerouslySetInnerHTML={parseHtmlString(memberCountriesWebPageData?.MEMBER_COUNTRIES?.description ?? '')} />
              </p>
            </div>
          </div>

          {/* Region Tabs and Countries */}
          <div className="space-y-16">
            {/* Region Tabs */}
            {isLoadingRegions ? (
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5 rounded overflow-hidden">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex-1 p-4 bg-gray-100 animate-pulse" />
                ))}
              </div>
            ) : regionsError ? (
              <div className="text-center py-8">
                <p className="text-red-600">Error loading regions. Please try again later.</p>
              </div>
            ) : regions.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5 rounded overflow-hidden">
                {regions.map((region) => (
                  <RegionTab
                    key={region.regionId}
                    region={normalizeRegionName(region.regionName)}
                    isActive={activeRegionId === region.regionId}
                    onClick={() => setActiveRegionId(region.regionId)}
                  />
                ))}
              </div>
            ) : null}

            {/* Region Content */}
            {!activeRegionId ? (
              <div className="text-center py-12">
                <p className="text-gray-600 text-lg">Please select a region to view member countries.</p>
              </div>
            ) : activeRegion ? (
              <div className="space-y-10">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                  <h2 className="text-3xl font-normal font-pt-serif text-gray-900 leading-loose">
                    {activeRegion.regionName}
                  </h2>
                </div>

                {/* Search and Countries */}
                <div className="space-y-8">
                  {/* Search */}
                  <div className="w-full md:w-72 relative">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search...."
                      className="w-full h-10 pl-4 pr-12 bg-gray-50 text-lg font-normal font-satoshi text-gray-900 placeholder-gray-600 rounded border border-gray-200 focus:outline-none focus:ring-2 focus:ring-wine-100 focus:border-transparent"
                    />
                    <div className="absolute right-0 top-0 w-10 h-10 bg-wine-100 rounded-r flex items-center justify-center">
                      <Search className="w-6 h-6 text-white" />
                    </div>
                  </div>

                  {/* Countries Grid */}
                  {isLoadingCountries ? (
                    <div className="space-y-8">
                      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 justify-items-center">
                        {[...Array(8)].map((_, i) => (
                          <div key={i} className="w-48 h-[215px] bg-gray-100 animate-pulse rounded" />
                        ))}
                      </div>
                    </div>
                  ) : countriesError ? (
                    <div className="text-center py-12">
                      <p className="text-red-600 mb-4">Error loading member countries. Please try again later.</p>
                    </div>
                  ) : filteredCountries.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-gray-600 text-lg">
                        {searchQuery
                          ? 'No countries found matching your search.'
                          : `No member countries found for ${activeRegion.regionName}.`}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-8">
                      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 justify-items-center">
                        {filteredCountries.map((country) => (
                          <CountryCard
                            key={country.id}
                            name={country.name}
                            fullName={country.fullName}
                            flag={country.flag}
                            onClick={() => handleCountryClick(country.id)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-10">
          <RecentArticles />
          {/* <StrategicPillars /> */}
          <Newsletter />
        </div>
      </div>
    </div>
  )
}

export default MemberCountries
